import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api.js";
import { ArrowLeft, Bell, Loader2, RefreshCw } from "lucide-react";
import PageHeader from "../components/PageHeader.jsx";

const KIND_LABELS = {
  "eta_passed":          "ETA ultrapassado",
  "w7_over_limit":       "W7 acima do limite",
  "docs_missing":        "Documentos em falta",
  "inspection_missing":  "Inspecção pendente",
  "grave_issue_open":    "Dano Grave em aberto",
  "ready_but_blocked":   "Pronta mas com bloqueio",
};

const KIND_TONE = {
  "eta_passed": "bg-amber-100 text-amber-800",
  "w7_over_limit": "bg-rose-100 text-rose-800",
  "docs_missing": "bg-sky-100 text-sky-800",
  "inspection_missing": "bg-slate-100 text-slate-700",
  "grave_issue_open": "bg-rose-100 text-rose-800",
  "ready_but_blocked": "bg-amber-100 text-amber-800",
};

export default function LogisticsAlertsPage() {
  const [alerts, setAlerts] = useState(null);
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, s] = await Promise.all([
        api.get("/logistics/alerts"),
        api.get("/logistics/alerts/summary"),
      ]);
      setAlerts(a.data); setSummary(s.data);
    } catch { setAlerts([]); }
  };
  useEffect(() => { load(); }, []);

  const runScan = async () => {
    setBusy(true);
    try { await api.post("/logistics/alerts/scan"); await load(); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-screen-xl mx-auto">
      <Link to="/logistica" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-ronil-navy mb-3"><ArrowLeft className="w-3.5 h-3.5" /> Logística</Link>
      <PageHeader
        overline="Operacional"
        title="Alertas da Logística"
        subtitle="Alertas automáticos enviados nas últimas 24h — ETA, W7, documentos e inspecções pendentes."
      />

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-5" data-testid="alerts-summary">
          {Object.entries(summary.last_24h).map(([k, v]) => (
            <div key={k} className={`rounded-md border px-3 py-2.5 ${KIND_TONE[k] || "bg-slate-50"} border-slate-200`}>
              <div className="text-[9px] uppercase tracking-widest font-heading font-semibold opacity-80">{KIND_LABELS[k] || k}</div>
              <div className="font-heading font-bold text-2xl leading-none mt-1 tabular-nums">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading font-bold text-ronil-navy text-sm uppercase tracking-widest">Últimos alertas</h3>
        <button onClick={runScan} disabled={busy} className="bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 rounded-md px-3 py-1.5 text-xs font-heading font-semibold inline-flex items-center gap-1" data-testid="rescan-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> {busy ? "A analisar…" : "Correr scan agora"}
        </button>
      </div>

      {alerts === null ? <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> A carregar…</div> : alerts.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center text-slate-400">Sem alertas abertos.</div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Enviado</th>
                <th className="px-3 py-2 text-left">VIN</th>
                <th className="px-3 py-2 text-left">Tipo</th>
                <th className="px-3 py-2 text-left">Detalhe</th>
                <th className="px-3 py-2 text-left">Destinatários</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map(a => (
                <tr key={a.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`alert-${a.id}`}>
                  <td className="px-3 py-1.5 text-xs">{(a.sent_at || "").replace("T", " ").slice(0, 16)}</td>
                  <td className="px-3 py-1.5 text-xs font-mono"><Link to={`/vehicles/${a.vin}`} className="text-ronil-navy hover:underline">{a.vin}</Link></td>
                  <td className="px-3 py-1.5 text-xs"><span className={`inline-flex px-1.5 py-0.5 rounded-sm text-[10px] font-heading font-bold uppercase tracking-wider ${KIND_TONE[a.alert_key] || "bg-slate-100"}`}>{KIND_LABELS[a.alert_key] || a.alert_key}</span></td>
                  <td className="px-3 py-1.5 text-xs">{a.detail}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">{(a.recipients || []).length} util.</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
